<template>
  <div class="operation-logs-page">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>操作日志</span>
          <div class="header-actions">
            <el-button
              v-if="isSuperAdminSession"
              class="btn-danger"
              size="small"
              :loading="clearing"
              @click="clearAllLogs"
            >一键清空</el-button>
            <el-button class="btn-secondary" size="small" @click="downloadExport">导出数据</el-button>
            <el-button class="btn-secondary" size="small" @click="loadLogs">刷新</el-button>
          </div>
        </div>
      </template>

      <div class="filter-row">
        <el-input
          v-model="filters.keyword"
          placeholder="搜索功能/接口/操作人"
          size="small"
          clearable
          class="filter-item keyword"
          @keyup.enter.native="onSearch"
        />
        <el-input
          v-model="filters.operator_name"
          placeholder="操作人"
          size="small"
          clearable
          class="filter-item"
          @keyup.enter.native="onSearch"
        />
        <el-select v-model="filters.page_name" placeholder="页面" size="small" clearable class="filter-item">
          <el-option v-for="p in pageOptions" :key="p" :label="p" :value="p" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          size="small"
          class="filter-item time"
        />
        <el-button type="primary" size="small" class="btn-primary" @click="onSearch">查询</el-button>
        <el-button size="small" class="btn-secondary" @click="onReset">重置</el-button>
      </div>

      <el-table :data="rows" stripe border style="width: 100%">
        <el-table-column label="操作时间" min-width="170">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column prop="page_name" label="页面" min-width="120" />
        <el-table-column prop="feature_name" label="功能" min-width="180" />
        <el-table-column prop="operator_name" label="操作人" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.operator_name || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="operator_role" label="角色" min-width="90" />
        <el-table-column prop="method" label="方法" width="90" />
        <el-table-column prop="path" label="接口" min-width="220" show-overflow-tooltip />
      </el-table>

      <admin-pagination
        :total="pagination.total"
        :page.sync="pagination.page"
        :page-size.sync="pagination.pageSize"
        @change="loadLogs"
      />
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'
import { formatApiError } from '../utils/apiError'
import { apiBase } from '../api/client'
import { getAdminToken, isSuperAdminUser } from '../utils/auth'
import { downloadBlobResponse } from '../utils/fileExport'

const PAGE_OPTIONS = [
  '登录',
  '订单管理',
  '随心订管理',
  '商品管理',
  '员工管理',
  '骑手管理',
  '门店考勤',
  '出勤日历',
  '审核申请'
]

function pad2(n) {
  return String(n).padStart(2, '0')
}

export default {
  name: 'OperationLogs',
  data() {
    return {
      rows: [],
      loading: false,
      clearing: false,
      dateRange: [],
      filters: {
        keyword: '',
        operator_name: '',
        page_name: ''
      },
      pagination: {
        page: 1,
        pageSize: 30,
        total: 0
      },
      pageOptions: PAGE_OPTIONS
    }
  },
  mounted() {
    this.loadLogs()
  },
  computed: {
    isSuperAdminSession() {
      return isSuperAdminUser()
    }
  },
  methods: {
    authHeaders() {
      const t = getAdminToken()
      return t ? { Authorization: `Bearer ${t}` } : {}
    },
    buildQuery() {
      const q = {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize
      }
      if (this.filters.keyword) q.keyword = this.filters.keyword
      if (this.filters.operator_name) q.operator_name = this.filters.operator_name
      if (this.filters.page_name) q.page_name = this.filters.page_name
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        q.start_at = this.dateRange[0]
        q.end_at = this.dateRange[1]
      }
      return q
    },
    async loadLogs() {
      this.loading = true
      try {
        const resp = await this.$api.get('/admin/operation-logs', { params: this.buildQuery() })
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          this.rows = Array.isArray(d.list) ? d.list : []
          this.pagination.total = Number(d.total || 0)
          this.pagination.page = Number(d.page || this.pagination.page || 1)
          this.pagination.pageSize = Number(d.pageSize || this.pagination.pageSize || 30)
        } else {
          this.$message.error((resp && resp.message) || '获取日志失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '获取日志失败'))
      } finally {
        this.loading = false
      }
    },
    async downloadExport() {
      try {
        const res = await axios.get(`${apiBase}/admin/operation-logs/export`, {
          params: this.buildQuery(),
          responseType: 'blob',
          headers: this.authHeaders()
        })
        downloadBlobResponse(res, '操作日志导出')
      } catch (e) {
        this.$message.error(formatApiError(e, '导出失败'))
      }
    },
    onSearch() {
      this.pagination.page = 1
      this.loadLogs()
    },
    onReset() {
      this.filters.keyword = ''
      this.filters.operator_name = ''
      this.filters.page_name = ''
      this.dateRange = []
      this.pagination.page = 1
      this.loadLogs()
    },
    async clearAllLogs() {
      try {
        await this.$confirm(
          '确定清空全部操作日志？此操作不可恢复，仅超级管理员可执行。',
          '一键清空',
          { type: 'warning', confirmButtonText: '确定清空', cancelButtonText: '取消' }
        )
      } catch (e) {
        return
      }
      this.clearing = true
      try {
        const resp = await this.$api.delete('/admin/operation-logs')
        if (resp && resp.code === 200) {
          this.$message.success((resp && resp.message) || '已清空')
          this.pagination.page = 1
          await this.loadLogs()
        } else {
          this.$message.error((resp && resp.message) || '清空失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '清空失败'))
      } finally {
        this.clearing = false
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-danger {
  color: #f56c6c;
  border-color: #fbc4c4;
  background: #fef0f0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-item {
  width: 180px;
}

.filter-item.keyword {
  width: 220px;
}

.filter-item.time {
  width: 360px;
}

.pagination-wrap {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
</style>
